import React, { useCallback, useId } from 'react';
import { DownOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export interface IFilterOption {
  value: string;
  label: string;
  /** Draws the right-aligned `default` hint; exactly one option per card carries it. */
  isDefault?: boolean;
  /**
   * Right-aligned control on this option's own row. Both special cases in the design — the custom
   * date pair and the six Demographics sub-selects — are the same structural need, so neither gets
   * a card of its own.
   */
  trailing?: React.ReactNode;
}

export interface IFilterCardProps {
  title: string;
  appliesTo: string;
  defaultLabel: string;
  /** Second header pair; only the Date Range card draws one ("Compared to: …"). */
  note?: { label: string; value: string };
  options: IFilterOption[];
  value: string;
  onChange: (value: string) => void;
  onReset: () => void;
  /**
   * Vertical space between option rows, and optionally a different one after the first row. Absent
   * means flush, which is what Date Range draws; only the caller knows what its own card specifies.
   */
  rowGap?: string;
  firstRowGap?: string;
  className?: string;
}

// Every measurement below is from design/filter-panel-spec-2026-08-11.md § 3, which is the parsed
// geometry of the eight card exports. None of them land on the token ramps, so they are literals
// named for the part they size. The width is a definite measure so the floating selector's
// shrink-to-fit resolves to the design's rather than to the chip it portals into (a60a3e9f9); the
// height is content-driven, since a popover is not the fixed-height card the export measured.
const CARD_WIDTH = '408px';
// Token `corner radius/13` on the card shell. A pixel pass read the arc as 10px and this briefly
// shipped at that; the scene graph is authoritative (filter-spec § 0 row 4).
const CARD_RADIUS = '13px';
// Figma's 17px text inset is the card's own 1px border plus 16px of padding, which is what puts the
// value box at x=17 and 374px wide inside the 408px shell (filter-spec § 2.1).
const CARD_INSET = '16px';
// `div.selectField`: 374 x 40 at x 17 y 38, radius token `corner radius/9`, fill the same #0D1428 as
// the chips, 1px #3E8FF0 border. The 7px above and below it are Figma's own element gaps — cardTop
// ends at y 31, the box runs 38→78 and `Applies to:` opens at y 85 (filter-spec § 2.2-2.3).
const VALUE_HEIGHT = '40px';
const VALUE_RADIUS = '9px';
const VALUE_INSET = '13px';
const VALUE_GAP = '7px';
const ROW_HEIGHT = '31px';
const ROW_RADIUS = '8px';
const ROW_INSET = '10px';
const PANEL_INSET = '6px';
const RADIO_SIZE = '14px';
const RADIO_DOT_SIZE = '8px';
const RADIO_GAP = '10px';
const SELECTED_ROW_TINT = tokens.color['filter-selected-row-tint'];

const Card = styled.article`
  display: flex;
  flex-direction: column;
  width: ${CARD_WIDTH};
  max-width: 100vw;
  padding-top: ${tokens.space['16']};
  overflow: hidden;
  border: 1px solid ${tokens.color['filter-border']};
  border-radius: ${CARD_RADIUS};
  background: ${tokens.color['surface-section']};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 ${CARD_INSET} ${tokens.space['12']};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const Title = styled.h3`
  margin: 0;
  color: ${tokens.color['filter-text']};
  font-size: ${tokens.type['card-description-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
`;

// Display-only: the choosing happens in the radio group below, and a second control for the same
// value would be a duplicate. It is a paragraph rather than a button or a select so nothing about it
// reads as operable, and the radio group points at it so the value still reaches a screen reader.
const ValueReadout = styled.p`
  display: flex;
  align-items: center;
  height: ${VALUE_HEIGHT};
  /* Its own margin rather than the Header's sibling rule: a child's margin declaration and
     Header > * + * both weigh (0,1,0) and this class is injected later, so the sibling gap paints
     0px here either way. Declaring it on the child is the only form that survives. */
  margin: ${VALUE_GAP} 0;
  padding: 0 ${VALUE_INSET};
  border: 1px solid ${tokens.color['filter-accent']};
  border-radius: ${VALUE_RADIUS};
  background: ${tokens.color['filter-surface']};
  box-shadow: ${tokens.elevation['filter-value-glow']};
  color: ${tokens.color['filter-text']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['body-line']};

  .anticon {
    margin-left: auto;
    font-size: 9px;
    color: ${tokens.color['filter-accent']};
  }
`;

const AnnotationLine = styled.p`
  display: flex;
  align-items: baseline;
  margin: 0;
  font-size: ${tokens.type['filter-annotation-size']};
  font-weight: ${tokens.type['filter-annotation-weight']};
  line-height: ${tokens.type['filter-annotation-line']};
`;

// The two annotation lines carry opposite emphasis — bright label / muted value on `Applies to:`,
// muted label / bright value on `Default:` — consistently across all eight cards (spec § 3.3).
// The bright half of each pair is also the bold one, in both lines: Figma bolds the `Applies to:`
// label and the `Default:` *value*, which are the two spans this wraps (filter-spec § 2.3, § 2.5).
const BrightText = styled.span`
  color: ${tokens.color['filter-text']};
  font-weight: ${tokens.type['card-title-weight']};
`;

const MutedText = styled.span`
  color: ${tokens.color['filter-text-muted']};
`;

// Right-aligned against the same inset the select field uses; only the Date Range card draws one.
const NotePair = styled.span`
  margin-left: auto;
  padding-left: ${tokens.space['8']};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${tokens.space['12']} ${PANEL_INSET} 0;
  border-top: 1px solid ${tokens.color['filter-border']};
  background: ${tokens.color['filter-surface']};
`;

const DefaultLine = styled(AnnotationLine)`
  margin: 0 ${tokens.space['12']} ${tokens.space['12']};
  font-size: ${tokens.type['filter-default-note-size']};
  font-weight: ${tokens.type['filter-default-note-weight']};
  line-height: ${tokens.type['filter-default-note-line']};
`;

/*
 * Row spacing is per card, not global. Date Range's rows sit at a 31px pitch — flush, no gap
 * (filter-spec § 2.6). Demographics is the card that separates them: rows at Top 12, 48, 90, 132,
 * 174, 216, all 31 tall, so 5px after the first row and 11px between the rest
 * (filter-demographics-open-devmode § 1). Reproduced as drawn rather than regularised to one value,
 * because a neighbour's spacing is not evidence for this one's.
 *
 * Doubled: `Row` declares no margin today, but a child that later does would beat a single-class
 * sibling rule on injection order and silently paint 0px — the trap this feature has hit nine times.
 */
const Options = styled.div<{ $rowGap?: string; $firstRowGap?: string }>`
  display: flex;
  flex-direction: column;

  ${({ $rowGap }) =>
    $rowGap &&
    `
    && > * + * {
      margin-top: ${$rowGap};
    }
  `}

  ${({ $firstRowGap }) =>
    $firstRowGap &&
    `
    && > *:nth-child(2) {
      margin-top: ${$firstRowGap};
    }
  `}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  min-height: ${ROW_HEIGHT};
  padding: 0 ${ROW_INSET};
  border-radius: ${ROW_RADIUS};

  &[data-selected='true'] {
    background: ${SELECTED_ROW_TINT};
  }
`;

const Radio = styled.input`
  flex: none;
  width: ${RADIO_SIZE};
  height: ${RADIO_SIZE};
  margin: 0;
  border: 1px solid ${tokens.color['filter-radio-border']};
  border-radius: 50%;
  background: ${tokens.color['control-surface']};
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;

  &:checked {
    border-color: ${tokens.color['filter-accent']};
    background: radial-gradient(
      ${tokens.color['filter-accent']} 0 calc(${RADIO_DOT_SIZE} / 2),
      ${tokens.color['control-surface']} calc(${RADIO_DOT_SIZE} / 2)
    );
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const RowLabel = styled.label`
  flex: 1 1 auto;
  margin-left: ${RADIO_GAP};
  color: ${tokens.color['filter-text']};
  font-size: ${tokens.type['card-description-size']};
  font-weight: ${tokens.type['card-description-weight']};
  line-height: ${tokens.type['card-description-line']};
  cursor: pointer;

  &[data-selected='true'] {
    color: ${tokens.color['filter-accent']};
    font-weight: ${tokens.type['card-title-weight']};
  }
`;

const DefaultHint = styled.span`
  flex: none;
  margin-left: ${tokens.space['8']};
  color: ${tokens.color['filter-text-muted']};
  font-size: ${tokens.type['filter-annotation-size']};
  font-weight: ${tokens.type['filter-annotation-weight']};
  line-height: ${tokens.type['filter-annotation-line']};
`;

// No vertical margin: Figma's rows are 31px whether or not they carry a control, and the sub-selects
// are themselves 31px. The 4px this used to hold was headroom for a 40px antd select and pushed
// every Demographics row to 39.
const Trailing = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  margin: 0 0 0 ${tokens.space['8']};

  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${tokens.space['12']} ${tokens.space['12']};
`;

const ResetButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['filter-accent']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['label-line']};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// One filter category: a header field showing the current value, the category's options as a real
// radio group, and a reset link that returns this card — and only this card — to its default.
// PLA-8815.
const FilterCard: React.FC<IFilterCardProps> = ({
  title,
  appliesTo,
  defaultLabel,
  note,
  options,
  value,
  onChange,
  onReset,
  rowGap,
  firstRowGap,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const groupId = useId();
  const readoutId = `${groupId}-value`;

  const handleRowChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.value),
    [onChange]
  );

  const selectedLabel = options.find(option => option.value === value)?.label ?? '';

  return (
    <Card className={className} aria-label={title} data-testid="filter-card">
      <Header>
        <Title data-testid="filter-card-title">{title}</Title>
        <ValueReadout id={readoutId} data-testid="filter-card-value">
          {selectedLabel}
          <DownOutlined aria-hidden />
        </ValueReadout>
        <AnnotationLine data-testid="filter-card-applies-to">
          <span>
            <BrightText>{t(`${TCustomerInsights}.Filters.AppliesToLabel`)}</BrightText>{' '}
            <MutedText>{appliesTo}</MutedText>
          </span>
          {note ? (
            <NotePair data-testid="filter-card-note">
              <BrightText>{note.label}</BrightText> <MutedText>{note.value}</MutedText>
            </NotePair>
          ) : null}
        </AnnotationLine>
      </Header>

      <Panel>
        <DefaultLine data-testid="filter-card-default">
          <span>
            <MutedText>{t(`${TCustomerInsights}.Filters.DefaultLabel`)}</MutedText>{' '}
            <BrightText>{defaultLabel}</BrightText>
          </span>
        </DefaultLine>

        <Options
          role="radiogroup"
          aria-label={title}
          aria-describedby={readoutId}
          $rowGap={rowGap}
          $firstRowGap={firstRowGap}
        >
          {options.map(option => {
            const selected = option.value === value;
            const rowId = `${groupId}-${option.value}`;

            return (
              <Row key={option.value} data-selected={selected} data-testid="filter-card-option">
                <Radio
                  type="radio"
                  id={rowId}
                  name={groupId}
                  value={option.value}
                  checked={selected}
                  onChange={handleRowChange}
                />
                <RowLabel htmlFor={rowId} data-selected={selected}>
                  {option.label}
                </RowLabel>
                {option.isDefault ? (
                  <DefaultHint data-testid="filter-card-default-hint">
                    {t(`${TCustomerInsights}.Filters.DefaultHint`)}
                  </DefaultHint>
                ) : null}
                {option.trailing ? (
                  <Trailing data-testid="filter-card-trailing">{option.trailing}</Trailing>
                ) : null}
              </Row>
            );
          })}
        </Options>

        <Foot>
          <ResetButton type="button" onClick={onReset} data-testid="filter-card-reset">
            {t(`${TCustomerInsights}.Filters.ResetToDefault`)}
          </ResetButton>
        </Foot>
      </Panel>
    </Card>
  );
};

export default FilterCard;
